import type { KeyboardEvent, MouseEvent, PropsWithChildren } from 'react'
import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import classNames from 'classnames'
import findIndex from 'lodash/findIndex'
import { Steps } from 'rsuite'
import { HorizontalScrollableBlock } from '..'
import type { IProgressSteps } from './interfaces'
import './styles.scss'

function renderSteps<T>({ steps, activeStep, onStepClick }: IProgressSteps<T>) {
  const activeIndex = findIndex(steps, ({ id }) => activeStep === id)

  return (
    <Steps current={activeIndex}>
      {steps.map(({ textId, text, id, title }, index) => {
        const key = typeof text === 'string' ? text : textId || (id as string | number)
        const isClickable = typeof onStepClick === 'function'

        const handleClick = isClickable
          ? (event: MouseEvent<HTMLDivElement>) => {
              const target = event.target as HTMLElement
              const interactiveArea = target.closest('.rs-steps-item-icon-wrapper, .rs-steps-item-title')

              if (!interactiveArea) return

              onStepClick(id, index)
            }
          : undefined

        const handleKeyDown = isClickable
          ? (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onStepClick(id, index)
              }
            }
          : undefined

        const interactiveProps = isClickable
          ? {
              onClick: handleClick,
              onKeyDown: handleKeyDown,
              tabIndex: 0,
              role: 'button' as const,
              className: 'progress-steps-item_clickable',
            }
          : {}

        return <Steps.Item description={textId ? <FormattedMessage id={textId} /> : text} title={title} key={key} {...interactiveProps} />
      })}
    </Steps>
  )
}

function BaseProgressSteps<T>({ steps, activeStep, className, onStepClick }: PropsWithChildren<IProgressSteps<T>>) {
  return <div className={classNames('progress-steps-container', className)}>{renderSteps({ steps, activeStep, onStepClick })}</div>
}

function ScrollableProgressSteps<T>({ steps, activeStep, className, onStepClick }: PropsWithChildren<IProgressSteps<T>>) {
  const [ref, setRef] = useState<HTMLElement | null>(null)
  const activeIndex = findIndex(steps, ({ id }) => activeStep === id)

  return (
    <HorizontalScrollableBlock refObject={ref} totalWidth={ref?.children[0]?.scrollWidth} total={steps.length} activeIndex={activeIndex}>
      <div className={classNames('progress-steps-container progress-steps-container_swipeable', className)} ref={setRef}>
        {renderSteps({ steps, activeStep, onStepClick })}
      </div>
    </HorizontalScrollableBlock>
  )
}

function ProgressSteps<T>(props: PropsWithChildren<IProgressSteps<T>>) {
  return props.withSwipeControls ? <ScrollableProgressSteps {...props} /> : <BaseProgressSteps {...props} />
}

export default ProgressSteps
